import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, LoginPayload, TokenResponse } from '@/types'
import * as authApi from '@/api/auth'

interface AuthState {
  token: string | null
  refreshToken: string | null
  user: User | null
  loading: boolean

  login: (payload: LoginPayload) => Promise<void>
  logout: () => void
  fetchMe: () => Promise<void>
  setUser: (user: User) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      user: null,
      loading: false,

      login: async (payload: LoginPayload) => {
        set({ loading: true })
        try {
          const data = await authApi.login(payload)
          set({
            token: data.access_token,
            refreshToken: data.refresh_token,
            user: data.user,
            loading: false,
          })
        } catch (e) {
          set({ loading: false })
          throw e
        }
      },

      logout: () => {
        // 安全加固（H1）：通知后端清除静态文件访问 Cookie（fire-and-forget，失败不影响登出）
        try { fetch('/api/v1/auth/logout', { method: 'POST' }) } catch { /* 忽略 */ }
        set({ token: null, refreshToken: null, user: null })
        // 清空跨账号缓存的项目数据，避免下个账号在「当前项目」里看到上个账号的项目
        // （动态引入避免 auth ↔ project 循环依赖）
        import('@/stores/project').then((m) => m.useProjectStore.getState().resetProjectState())
      },

      fetchMe: async () => {
        try {
          const user = await authApi.getMe()
          set({ user })
        } catch (e) {
          set({ token: null, user: null })
        }
      },

      setUser: (user: User) => set({ user }),
    }),
    {
      name: 'auroratest-auth',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    },
  ),
)
